/**
 * Pure bind from the LG-2849 derived model onto today's `Transcript` snapshot + `TranscriptEvent`s
 * (LG-2850). No Yjs here — inputs are `TimedParagraph`s plus sidecar snapshots, so this is unit-tested
 * without a doc.
 *
 * Identity contract: a `TranscriptParagraph` (and its `timings[id]` array, and the `paragraphIds`
 * array, and `speakers`) keeps its previous object reference when its derived value is unchanged, so
 * `useTranscriptParagraph` / `useTranscriptSelector` don't re-render on untouched paragraphs.
 *
 * Only read-only rendering (`ParagraphTextReadonly` via `useTranscriptParagraph`) consumes these
 * `map` events. The open editor (`ParagraphTextEditable`) is updated from
 * `RtBrokerTranscriptClientV2.subscribeRemoteParagraphText`, not from `text` events.
 */

import {
    Transcript,
    TranscriptParagraph,
    TranscriptParagraphContent,
    TranscriptSpeaker,
    TranscriptTimings,
    WordTiming,
} from 'src/models/Transcript'
import { TranscriptEvent } from 'src/models/TranscriptEvent'
import type { TimedParagraph } from 'src/models/crdtV2'

export type SnapshotInput = {
    /** Ordered, authoritative derived paragraphs (`ApplyResult.index.paragraphs`). */
    paragraphs: readonly TimedParagraph[]
    dirtyIds: readonly string[]
    addedIds: readonly string[]
    removedIds: readonly string[]
    speakers: Record<string, TranscriptSpeaker>
    recordingStartedAt: number
}

export type SnapshotResult = { transcript: Transcript; events: TranscriptEvent[] }

const mapEvent = (path: (string | number)[]): TranscriptEvent => ({ type: 'map', isLocal: false, tags: [], path, delta: [{ change: [] }] })

const arrayEvent = (path: (string | number)[]): TranscriptEvent => ({ type: 'array', isLocal: false, tags: [], path, delta: [] })

const toContent = (paragraph: TimedParagraph): TranscriptParagraphContent[] =>
    paragraph.runs.map((run) => ({ text: run.text, isEditable: run.finalized }))

const toParagraph = (paragraph: TimedParagraph, recordingStartedAt: number): TranscriptParagraph => {
    const { startTime, endTime } = paragraph
    const timestampAbsolute = startTime !== null && Number.isFinite(recordingStartedAt) ? recordingStartedAt + startTime : null

    return {
        id: paragraph.id,
        speakerId: paragraph.speakerId,
        qac: paragraph.qac,
        timestampAbsolute,
        timestampRelative: startTime,
        startTime,
        endTime,
        content: toContent(paragraph),
    }
}

const toWordTimings = (paragraph: TimedParagraph): WordTiming[] => {
    const timings: WordTiming[] = []

    for (const run of paragraph.runs) {
        for (const word of run.words) {
            if (word.start !== null && word.end !== null) {
                timings.push({
                    mediaStart: word.start,
                    mediaEnd: word.end,
                    charStart: word.charStart,
                    charEnd: word.charEnd,
                    paragraphId: paragraph.id,
                })
            }
        }
    }

    return timings
}

const eqContent = (a: TranscriptParagraphContent[], b: TranscriptParagraphContent[]): boolean =>
    a.length === b.length && a.every((entry, i) => entry.text === b[i].text && entry.isEditable === b[i].isEditable)

const eqParagraph = (a: TranscriptParagraph, b: TranscriptParagraph): boolean =>
    a.speakerId === b.speakerId &&
    a.qac === b.qac &&
    a.timestampAbsolute === b.timestampAbsolute &&
    a.timestampRelative === b.timestampRelative &&
    a.startTime === b.startTime &&
    a.endTime === b.endTime &&
    eqContent(a.content, b.content)

const eqWordTimings = (a: WordTiming[], b: WordTiming[]): boolean =>
    a.length === b.length &&
    a.every(
        (word, i) =>
            word.mediaStart === b[i].mediaStart &&
            word.mediaEnd === b[i].mediaEnd &&
            word.charStart === b[i].charStart &&
            word.charEnd === b[i].charEnd,
    )

const eqIds = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((id, i) => id === b[i])

const diffSpeakers = (
    prev: Record<string, TranscriptSpeaker>,
    next: Record<string, TranscriptSpeaker>,
): { changed: boolean; changedIds: string[] } => {
    const changedIds: string[] = []
    const ids = new Set<string>([...Object.keys(prev), ...Object.keys(next)])

    for (const id of ids) {
        const a = prev[id]
        const b = next[id]
        if (a === undefined || b === undefined || a.name !== b.name) {
            changedIds.push(id)
        }
    }

    return { changed: changedIds.length > 0, changedIds }
}

/**
 * Fold a projection apply (plus sidecar snapshots) into the next `Transcript` and the events to emit.
 * `recordingStartedAt` changing recomputes every paragraph's `timestampAbsolute`, so all paragraphs
 * are reconsidered that turn (only those whose value actually moves emit an event).
 */
export const bindSnapshot = (prev: Transcript, input: SnapshotInput): SnapshotResult => {
    const recordingChanged = prev.recordingStartedAt !== input.recordingStartedAt
    const rebuildSet = new Set<string>([...input.dirtyIds, ...input.addedIds])

    const nextParagraphs: Record<string, TranscriptParagraph> = {}
    const nextTimings: TranscriptTimings = {}
    const paragraphEvents: TranscriptEvent[] = []
    const timingsEvents: TranscriptEvent[] = []
    let paragraphsRebuilt = false
    let timingsRebuilt = false

    for (const timed of input.paragraphs) {
        const { id } = timed
        const prevParagraph = prev.paragraphs[id]
        const reconsider = rebuildSet.has(id) || recordingChanged || prevParagraph === undefined

        if (!reconsider) {
            nextParagraphs[id] = prevParagraph
            nextTimings[id] = prev.timings[id] ?? []
            continue
        }

        const builtParagraph = toParagraph(timed, input.recordingStartedAt)
        if (prevParagraph !== undefined && eqParagraph(prevParagraph, builtParagraph)) {
            nextParagraphs[id] = prevParagraph
        } else {
            nextParagraphs[id] = builtParagraph
            paragraphEvents.push(mapEvent(['paragraphs', id]))
            paragraphsRebuilt = true
        }

        const builtTimings = toWordTimings(timed)
        const prevTimings = prev.timings[id]
        if (prevTimings !== undefined && eqWordTimings(prevTimings, builtTimings)) {
            nextTimings[id] = prevTimings
        } else {
            nextTimings[id] = builtTimings
            timingsEvents.push(mapEvent(['timings', id]))
            timingsRebuilt = true
        }
    }

    const nextIds = input.paragraphs.map((paragraph) => paragraph.id)
    const idsChanged = !eqIds(prev.paragraphIds, nextIds)
    const paragraphIds = idsChanged ? nextIds : prev.paragraphIds

    // Preserve the container reference when nothing in it changed (membership/order via `idsChanged`,
    // contents via the rebuilt flags), so `toJS('paragraphs')` / `toJS('timings')` stay stable for
    // unrelated transactions — matching how `paragraphIds` and `speakers` are handled, and honoring
    // the `toJS` "same reference unless changed" contract that whole-container selectors rely on.
    const paragraphs = idsChanged || paragraphsRebuilt ? nextParagraphs : prev.paragraphs
    const timings = idsChanged || timingsRebuilt ? nextTimings : prev.timings

    const { changed: speakersChanged, changedIds: speakerChangedIds } = diffSpeakers(prev.speakers, input.speakers)
    const speakers = speakersChanged ? input.speakers : prev.speakers

    const events: TranscriptEvent[] = []
    if (idsChanged) {
        events.push(arrayEvent(['paragraphIds']))
    }
    events.push(...paragraphEvents, ...timingsEvents)
    if (speakersChanged) {
        // Emit both the collection event (for `useTranscriptSpeakers` on `speakers`) and a scoped
        // event per changed id: `emitEvents` fans out only to ancestor paths, so a `useTranscriptSpeaker(id)`
        // subscriber on `speakers.${id}` is not reached by the collection event alone.
        events.push(mapEvent(['speakers']))
        for (const id of speakerChangedIds) {
            events.push(mapEvent(['speakers', id]))
        }
    }
    if (recordingChanged) {
        events.push(mapEvent(['recordingStartedAt']))
    }

    const transcript: Transcript = {
        paragraphIds,
        paragraphs,
        speakers,
        recordingStartedAt: input.recordingStartedAt,
        timings,
    }

    return { transcript, events }
}
